<template>
  <header ref="headerRef" class="border-b">
    <ClientOnly>
      <div
        v-if="showBanner"
        class="bg-yellow-400 border-b border-yellow-500 text-black text-sm text-center p-2 flex items-center justify-center"
      >
        <span v-if="isIOS" class="mr-2">
          For the best experience, download the grep app from the App Store.
        </span>
        <span v-else class="mr-2">
          grep is not yet optimized for mobile. For the best experience, please use a desktop
          browser.
        </span>
        <button class="ml-2 underline text-xs" @click="dismissBanner">Dismiss</button>
      </div>
    </ClientOnly>
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex h-16 items-center justify-between">
        <div class="flex items-center">
          <div class="flex items-center">
            <img
              src="/logo-dark.svg"
              alt="grep Logo"
              class="h-8 w-8 mr-2 block dark:hidden"
              width="32"
              height="32"
            />
            <img
              src="/logo-white.svg"
              alt="grep Logo"
              class="h-8 w-8 mr-2 hidden dark:block"
              width="32"
              height="32"
            />
            <ClientOnly>
              <AppLink :to="logoLink" class="text-2xl font-bold text-foreground"> grep </AppLink>
            </ClientOnly>
          </div>
        </div>
        <nav class="hidden sm:block ml-auto">
          <ul class="flex space-x-4 justify-end">
            <ClientOnly>
              <template v-if="isLoggedIn">
                <!-- Navigation for logged-in users (stays the same) -->
              </template>
            </ClientOnly>
          </ul>
        </nav>
        <div class="flex items-center space-x-8">
          <ClientOnly>
            <template v-if="isLoggedIn">
              <button
                :disabled="!isProductlaneReady"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                :class="[
                  isProductlaneReady
                    ? 'bg-red-100 text-red-800 hover:bg-red-200'
                    : 'bg-gray-100 text-gray-500 cursor-not-allowed',
                ]"
                @click="openFeedback"
              >
                <span class="mr-1">Feedback</span>
                <Icon
                  :name="
                    isProductlaneReady ? 'heroicons:chat-bubble-left-right' : 'heroicons:clock'
                  "
                  class="w-3 h-3"
                />
              </button>
              <div
                class="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {{ userStore.capitalizedRole }} User
              </div>
            </template>
          </ClientOnly>
          <ClientOnly>
            <template v-if="!isLoading">
              <UserDropDown v-if="isLoggedIn" :key="userDropdownKey" />
              <div v-else-if="!isLoggedIn" class="flex items-center">
                <AppLink
                  to="/auth"
                  class="bg-black text-white sm:px-6 sm:py-3 px-4 py-2 text-sm sm:text-base rounded-lg font-medium hover:bg-gray-800 transition-all duration-300 inline-block shadow-md ring-1 ring-black ring-opacity-5 backdrop-blur-sm"
                >
                  <span class="hidden sm:inline">Join today</span>
                  <span class="sm:hidden">Join today</span>
                </AppLink>
              </div>
            </template>
          </ClientOnly>
        </div>
      </div>
    </div>
    <ClientOnly>
      <!-- Mobile menu removed -->
    </ClientOnly>
  </header>
</template>

<script setup>
import { watch, ref, computed, onMounted, nextTick } from 'vue'
import { useUserStore } from '@/stores/user'
import { useNuxtApp } from '#app'
import { useMobileBanner } from '@/composables/useMobileBanner'

const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn ?? false)
const { showBanner, dismissBanner, isIOS } = useMobileBanner(isLoggedIn)

const { $productlane } = useNuxtApp()
const isLoading = ref(true)
const userDropdownKey = ref(0)
const headerRef = ref(null)

const logoLink = computed(() => (isLoggedIn.value ? '/dashboard/home' : '/'))
const isProductlaneReady = computed(() => $productlane?.isInitialized?.value ?? false)

const openFeedback = () => {
  if (isProductlaneReady.value) {
    $productlane.open()
  }
}

onMounted(async () => {
  await userStore.checkLoginStatus()
  isLoading.value = false

  await nextTick()
  if (headerRef.value) {
    if (!isLoggedIn.value) {
      headerRef.value.classList.remove('border-b')
      headerRef.value.classList.add('border-b-0')
    } else {
      headerRef.value.classList.add('border-b')
      headerRef.value.classList.remove('border-b-0')
    }
  }
})

watch(
  isLoggedIn,
  (loggedIn) => {
    if (headerRef.value) {
      if (!loggedIn) {
        headerRef.value.classList.remove('border-b')
        headerRef.value.classList.add('border-b-0')
      } else {
        headerRef.value.classList.add('border-b')
        headerRef.value.classList.remove('border-b-0')
      }
    }
    if (loggedIn) {
      userDropdownKey.value += 1
    }
  },
  { flush: 'post' }
)
</script>
